import NiceModal, { useModal } from "@ebay/nice-modal-react";
import cx from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    BOARD_ACCESS_GET_V2,
    BOARD_MEMBERS_ACCESS_SET_V2,
    BOARD_MEMBERS_ADD_V2,
    BOARD_MEMBERS_DELETE_V2,
    BOARD_SHARE_GET_V2,
    BOARD_SHARE_LINK_ACCESS_SET_V2,
    BOARD_SHARE_TEAM_ACCESS_SET,
} from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { useResizeWindow } from "@/helpers/hooks/useResizeWindow";
import { MembersIcon, Planet } from "@/icons";
import { ChangePassword } from "@/interface/ShareWindow/ChangePassword/ChangePassword";
import { MemberAccessList } from "@/interface/ShareWindow/MemberAccessList/MemberAccessList";
import {
    PermissionSelect,
    type SelectOption,
} from "@/interface/ShareWindow/PermissionSelect/PermissionSelect";
import { SearchMember } from "@/interface/ShareWindow/SearchMember/SearchMember";
import { InputSkeleton } from "@/interface/ShareWindow/Skeletons/InputSkeleton";
import { UserSkeleton } from "@/interface/ShareWindow/Skeletons/UserSkeleton";
import {
    AccessDeleteEnum,
    AccessEnum,
    BoardAccessEnum,
    type BoardShareResponse,
    getBoardAccessEnumToText,
    type MemberAccessInterface,
    type Option,
} from "@/interface/ShareWindow/types";
import { getBoardOptions } from "@/interface/ShareWindow/utils";
import { Button } from "@/shared/ui/Button";
import { showSnackbar } from "@/snackbar/snackbar";
import styles from "./ShareWindow.module.scss";

type ShareModalProps = {
    onlyRead: boolean;
};

export const ShareWindow = NiceModal.create<ShareModalProps>(({ onlyRead }) => {
    const { t } = useTranslation();
    const { remove } = useModal();
    const windowSize = useResizeWindow();
    const [modalHeight, setModalHeight] = useState("670px");
    const [isMemberChanged, setIsMemberChanged] = useState(false);
    const [data, setData] = useState<BoardShareResponse>();
    const [isLoading, setIsLoading] = useState(false);

    const canEdit = [AccessEnum.edit, AccessEnum.owner];
    const canIEdit = data?.access && canEdit.includes(data?.access as AccessEnum);

    const boardAccessEnumToText = getBoardAccessEnumToText();
    const boardOptions = getBoardOptions(onlyRead);

    const onClose = () => {
        remove();
    };
    const getData = useCallback(async () => {
        const result = await MakeRequest(BOARD_SHARE_GET_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
        });
        setData(result as BoardShareResponse);
    }, []);

    const callbackHandler = async ({ members, access }: MemberAccessInterface) => {
        await MakeRequest(BOARD_MEMBERS_ADD_V2, {
            members,
            access,
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
        });
        await getData();

        showSnackbar(t("share.notifyUsersAdded"), { type: "success", duration: SNACKBAR_TIME });
        remove();
    };
    const copyLink = async () => {
        try {
            if (navigator.permissions !== undefined && navigator.permissions.query !== undefined) {
                await navigator.permissions.query({ name: "clipboard-write" as PermissionName });
            }

            await navigator.clipboard.writeText(
                `${canv.host}/board/${canv.getInitDataProp("boardUID")}`,
            );
            showSnackbar(t("share.notifyCopied"), { type: "success", duration: SNACKBAR_TIME });
        } catch {
            showSnackbar(t("share.notifyCopyError"), { type: "error", duration: SNACKBAR_TIME });
        }
    };

    const teamDefaultValue: SelectOption<BoardAccessEnum> =
        data && data.accessByTeam !== "owner"
            ? {
                  value: data.accessByTeam as BoardAccessEnum,
                  label: boardAccessEnumToText[data.accessByTeam as BoardAccessEnum],
              }
            : boardOptions[0];

    const linkDefaultValue = data
        ? {
              value: data.accessByLink as BoardAccessEnum,
              label: boardAccessEnumToText[data.accessByLink as "view" | "edit" | "denied"],
          }
        : boardOptions[0];

    const handleMemberAccess = async (access: Option, accountId: number) => {
        if (access.value === AccessDeleteEnum.delete) {
            await MakeRequest(BOARD_MEMBERS_DELETE_V2, {
                token: canv.getInitDataProp("userToken"),
                boardUID: canv.getInitDataProp("boardUID"),
                memberId: accountId,
            });
        } else {
            await MakeRequest(BOARD_MEMBERS_ACCESS_SET_V2, {
                token: canv.getInitDataProp("userToken"),
                boardUID: canv.getInitDataProp("boardUID"),
                access: access.value as AccessEnum,
                memberId: accountId,
            });
        }
        await getData();
    };

    const validateAndUpdateAccess = async () => {
        const result = await MakeRequest(BOARD_ACCESS_GET_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
        });
        if (result?.access && canEdit.includes(result?.access as AccessEnum)) {
            await getData();
        } else {
            remove();
        }
    };

    const handleAccessAllInTeam = async (value: Option) => {
        await MakeRequest(BOARD_SHARE_TEAM_ACCESS_SET, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
            access: value.value as BoardAccessEnum,
        });
        await validateAndUpdateAccess();
    };

    const handleBoardAccessLink = async (value: Option) => {
        await MakeRequest(BOARD_SHARE_LINK_ACCESS_SET_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
            access: value.value as BoardAccessEnum,
        });
        await getData();
    };

    const toggleVisibility = useCallback((v) => setIsMemberChanged(v), []);

    useEffect(() => {
        if (windowSize.height && windowSize.height < 720) {
            setModalHeight(`${windowSize.height - 60}px`);
        } else {
            setModalHeight("670px");
        }
    }, [windowSize]);

    useEffect(() => {
        setIsLoading(true);
        getData().finally(() => setIsLoading(false));
    }, [getData]);

    const isFirstLoad = !data && isLoading;

    return (
        <div className={styles.shareWindowWrapper} onClick={onClose}>
            <div
                className={styles.shareWindow}
                onClick={(e) => e.stopPropagation()}
                style={{ height: isMemberChanged ? modalHeight : "auto" }}
            >
                <div className={styles.container}>
                    {isFirstLoad ? (
                        <div className={styles.shareWindowSearch}>
                            <InputSkeleton />
                        </div>
                    ) : (
                        <SearchMember
                            toggleVisibility={toggleVisibility}
                            onClose={onClose}
                            callback={callbackHandler}
                            teamId={data?.teamId || 0}
                        />
                    )}

                    {!isMemberChanged && (
                        <div className={styles.share}>
                            {isFirstLoad ? (
                                <div className={styles.shareWindowSearch}>
                                    <InputSkeleton />
                                </div>
                            ) : (
                                <div className={styles.allInTeam}>
                                    <div className={styles.icon}>
                                        <MembersIcon className={styles.add} />
                                    </div>
                                    <div className={styles.title}>
                                        {t("share.allTeam")} <span>{data?.teamName || ""}</span>
                                    </div>
                                    <div className={styles.blueSelect}>
                                        {canIEdit && (
                                            <PermissionSelect<BoardAccessEnum>
                                                options={boardOptions}
                                                disabled={isLoading}
                                                selectedValue={teamDefaultValue}
                                                onChange={handleAccessAllInTeam}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}

                            {isFirstLoad ? (
                                <div className={styles.shareWindowSearchWide}>
                                    <InputSkeleton />
                                </div>
                            ) : (
                                <div
                                    className={styles.allInTeam}
                                    style={{
                                        display: data?.allowControlByLink === "allow" ? "" : "none",
                                    }}
                                >
                                    <div className={styles.icon}>
                                        <Planet />
                                    </div>
                                    <div className={styles.title}>{t("share.linkAccess")}</div>
                                    <div className={styles.blueSelect}>
                                        {data && (
                                            <PermissionSelect<BoardAccessEnum>
                                                options={boardOptions}
                                                disabled={isLoading}
                                                selectedValue={linkDefaultValue}
                                                onChange={handleBoardAccessLink}
                                            />
                                        )}
                                    </div>
                                    <div></div>
                                    {linkDefaultValue.value !== BoardAccessEnum.denied && (
                                        <ChangePassword />
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {isFirstLoad ? (
                        <UserSkeleton />
                    ) : (
                        !isMemberChanged && (
                            <MemberAccessList
                                members={data?.members || []}
                                handler={handleMemberAccess}
                                canEdit={Boolean(canIEdit)}
                            />
                        )
                    )}

                    {!isMemberChanged && (
                        <div className={styles.actions}>
                            <Button
                                type="submit"
                                onClick={copyLink}
                                className={styles.actionButtons}
                            >
                                {t("share.copyBoardLink")}
                            </Button>
                            <Button
                                onClick={onClose}
                                variant="tertriary"
                                style={{ padding: "16px" }}
                                className={cx(styles.actionButtons, styles.outLine)}
                            >
                                {t("share.close")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
